import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';
import { useGameBalance } from './hooks/useGameBalance';
import { useMultiplier } from './hooks/useMultiplier';
import { useSounds } from './hooks/useSounds';
import { formatLilape } from './config/gameConfig';
import { DepositWithdrawPanel, DepositButton } from './components/DepositWithdrawPanel';

/* Constants */
const WAIT_MS = 5000;
const GROWTH = 0.072;
const WIN_RATE = 0.03; // 3% chance to win (not crash at 1x)
const BET_OPTIONS = [20_000_000, 50_000_000, 100_000_000, 500_000_000];
const AUTO_TARGETS = [1.5, 2, 3, 5, 10];

/* Helpers */
function genCrash() {
  // 97% chance to crash at 1.00-1.50x (lose)
  // 3% chance to go higher (win potential)
  const r = Math.random();
  if (r > WIN_RATE) {
    // Crash early - between 1.00 and 1.50
    return Math.round((1.00 + Math.random() * 0.50) * 100) / 100;
  }
  // Lucky round - can go higher
  return Math.max(1.51, Math.round(100 / (1 - r)) / 100);
}

function multColor(m) {
  if (m >= 10) return '#A855F7';
  if (m >= 5) return '#7C3AED';
  if (m >= 2) return '#10B981';
  if (m >= 1.5) return '#F59E0B';
  return '#EF4444';
}

function fmtMult(m) { return m.toFixed(2) + '×'; }

/* Pre-generated stars */
const STARS = Array.from({ length: 90 }, () => ({
  x: Math.random(), y: Math.random(),
  r: Math.random() * 1.4 + 0.3,
  a: Math.random() * 0.6 + 0.2,
}));

/* Draw parachutist */
function drawParachutist(ctx, x, y, frame, cashMult) {
  const sway = Math.sin(frame * 0.06) * 4;
  const px = x + sway;
  const py = y;

  const openP = Math.min(frame / 18, 1);
  const domeR = 30 * openP;
  const domeY = py - 48 * openP;

  if (domeR > 3) {
    const COLORS = ['#F59E0B', '#fff', '#7C3AED', '#fff', '#F59E0B', '#fff', '#7C3AED', '#fff'];
    for (let i = 0; i < 8; i++) {
      const a0 = Math.PI + (i / 8) * Math.PI;
      const a1 = Math.PI + ((i + 1) / 8) * Math.PI;
      ctx.beginPath();
      ctx.moveTo(px, domeY);
      ctx.arc(px, domeY, domeR, a0, a1);
      ctx.closePath();
      ctx.globalAlpha = 0.92;
      ctx.fillStyle = COLORS[i];
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    ctx.beginPath();
    ctx.arc(px, domeY, domeR, Math.PI, 0);
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 0.8;
    for (let i = 0; i < 5; i++) {
      const sx = px - domeR + (i / 4) * domeR * 2;
      ctx.beginPath();
      ctx.moveTo(sx, domeY + 2);
      ctx.lineTo(px, py - 3);
      ctx.stroke();
    }
  }

  ctx.shadowColor = '#60A5FA';
  ctx.shadowBlur = 8;

  ctx.fillStyle = '#CBD5E1';
  ctx.beginPath();
  ctx.roundRect(px - 6, py + 4, 12, 14, 3);
  ctx.fill();

  ctx.fillStyle = 'rgba(96,165,250,0.3)';
  ctx.fillRect(px - 5, py + 6, 10, 4);

  ctx.fillStyle = '#E2E8F0';
  ctx.beginPath();
  ctx.arc(px, py - 1, 8, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#1E3A5F';
  ctx.strokeStyle = '#60A5FA';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(px + 1, py - 1, 5, 4, 0, -0.6, 0.6);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.beginPath();
  ctx.ellipse(px - 1, py - 3, 2, 1.5, -0.5, 0, Math.PI * 2);
  ctx.fill();

  ctx.shadowBlur = 0;

  ctx.strokeStyle = '#CBD5E1';
  ctx.lineWidth = 3.5;
  ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(px - 6, py + 8); ctx.lineTo(px - 13, py + 2); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(px + 6, py + 8); ctx.lineTo(px + 13, py + 2); ctx.stroke();

  ctx.fillStyle = '#94A3B8';
  ctx.beginPath(); ctx.arc(px - 13, py + 2, 3, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(px + 13, py + 2, 3, 0, Math.PI * 2); ctx.fill();

  const legSway = Math.sin(frame * 0.1) * 2;
  ctx.lineWidth = 3.5;
  ctx.beginPath(); ctx.moveTo(px - 3, py + 18); ctx.lineTo(px - 4 + legSway, py + 28); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(px + 3, py + 18); ctx.lineTo(px + 4 - legSway, py + 28); ctx.stroke();

  ctx.fillStyle = '#475569';
  ctx.beginPath(); ctx.ellipse(px - 4 + legSway, py + 30, 4, 2.5, 0.3, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(px + 4 - legSway, py + 30, 4, 2.5, -0.3, 0, Math.PI * 2); ctx.fill();

  if (cashMult && frame > 10) {
    const badgeAlpha = Math.min((frame - 10) / 10, 1);
    ctx.globalAlpha = badgeAlpha;
    const label = `✓ ${fmtMult(cashMult)}`;
    ctx.font = 'bold 10px Inter, sans-serif';
    const tw = ctx.measureText(label).width;
    const bx = px + 18, by = py - 6;
    ctx.fillStyle = '#10B981';
    ctx.beginPath();
    ctx.roundRect(bx - 3, by - 9, tw + 10, 14, 5);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(label, bx + 2, by);
    ctx.globalAlpha = 1;
  }
}

export function CrashGame({ onBack }) {
  const { hapticFeedback } = useTelegram();
  const { connected, sendTransaction, address } = useTon();
  const gameBalanceHook = useGameBalance(address, sendTransaction);
  const { gameBalance, useSpin, addWinnings, canSpin } = gameBalanceHook;
  const { applyMultiplier } = useMultiplier();
  const { play, startRocketSound, updateRocketSound, stopRocketSound } = useSounds();

  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const crashRef = useRef(null);
  const pointsRef = useRef([]);

  const activeBetRef = useRef(null);
  const autoValRef = useRef(0);
  const cashedRef = useRef(false);
  const parachutistRef = useRef(null);

  const [phase, setPhase] = useState('waiting');
  const [countdown, setCd] = useState(5);
  const [mult, setMult] = useState(1.00);
  const [bet, setBet] = useState(BET_OPTIONS[0]);
  const [autoTarget, setAutoTarget] = useState(2);
  const [activeBet, setActiveBet] = useState(null);
  const [cashedOut, setCashedOut] = useState(false);
  const [wonAt, setWonAt] = useState(null);
  const [wonPts, setWonPts] = useState(0);
  const [lostBet, setLostBet] = useState(null);
  const [history, setHistory] = useState([2.31, 1.05, 8.76, 1.42, 3.20, 4.55]);
  const [flashRed, setFlashRed] = useState(false);
  const [showWallet, setShowWallet] = useState(false);

  useEffect(() => { autoValRef.current = autoTarget; }, [autoTarget]);

  /* Canvas draw */
  const drawCanvas = useCallback((pts, curMult, crashed) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;

    ctx.fillStyle = '#030714'; ctx.fillRect(0, 0, W, H);

    const neb = ctx.createRadialGradient(W * .85, H * .15, 0, W * .85, H * .15, W * .5);
    neb.addColorStop(0, 'rgba(124,58,237,0.09)'); neb.addColorStop(1, 'transparent');
    ctx.fillStyle = neb; ctx.fillRect(0, 0, W, H);

    STARS.forEach(s => {
      ctx.beginPath(); ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${s.a})`; ctx.fill();
    });

    if (pts.length < 2) return;

    const maxT = Math.max(pts[pts.length - 1].t, 1);
    const maxM = Math.max(curMult, 2) * 1.18;
    const PL = 32, PB = 28, PR = 16, PT = 28;
    const PW = W - PL - PR, PH = H - PT - PB;

    const cx = t => PL + (t / maxT) * PW;
    const cy = m => PT + PH - ((m - 1) / (maxM - 1)) * PH;

    [2, 3, 5, 10, 20].filter(g => g <= maxM * 1.05).forEach(g => {
      const y = cy(g);
      ctx.strokeStyle = 'rgba(255,255,255,0.04)'; ctx.lineWidth = 1;
      ctx.setLineDash([3, 7]);
      ctx.beginPath(); ctx.moveTo(PL, y); ctx.lineTo(W - PR, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.font = 'bold 9px Inter,sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(`${g}×`, 3, y + 3);
    });

    const last = pts[pts.length - 1];
    const lineColor = crashed ? '#EF4444' : '#F59E0B';

    const fG = ctx.createLinearGradient(0, cy(maxM), 0, cy(1));
    const fa = crashed ? 'rgba(239,68,68,' : 'rgba(245,158,11,';
    fG.addColorStop(0, fa + '0.28)'); fG.addColorStop(0.7, fa + '0.06)'); fG.addColorStop(1, fa + '0)');
    ctx.beginPath();
    ctx.moveTo(cx(pts[0].t), cy(pts[0].m));
    pts.forEach(p => ctx.lineTo(cx(p.t), cy(p.m)));
    ctx.lineTo(cx(last.t), cy(1)); ctx.lineTo(cx(pts[0].t), cy(1)); ctx.closePath();
    ctx.fillStyle = fG; ctx.fill();

    ctx.save();
    ctx.strokeStyle = lineColor; ctx.lineWidth = 3; ctx.lineJoin = 'round';
    ctx.shadowColor = lineColor; ctx.shadowBlur = crashed ? 6 : 16;
    ctx.beginPath();
    pts.forEach((p, i) => i === 0 ? ctx.moveTo(cx(p.t), cy(p.m)) : ctx.lineTo(cx(p.t), cy(p.m)));
    ctx.stroke(); ctx.restore();

    const tx = cx(last.t), ty = cy(last.m);

    const pr = parachutistRef.current;
    if (pr && pr.spawning) {
      pr.x = tx; pr.y = ty;
      pr.vy = -2.5;
      pr.vx = -(1.8 + Math.random() * 0.8);
      pr.spawning = false;
    }

    if (crashed) {
      ctx.save(); ctx.translate(tx, ty);
      const eg = ctx.createRadialGradient(0, 0, 0, 0, 0, 32);
      eg.addColorStop(0, 'rgba(255,120,0,0.85)'); eg.addColorStop(0.4, 'rgba(255,40,0,0.4)'); eg.addColorStop(1, 'transparent');
      ctx.fillStyle = eg; ctx.beginPath(); ctx.arc(0, 0, 32, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#FF4444'; ctx.beginPath(); ctx.arc(0, 0, 11, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#FF8800'; ctx.lineWidth = 2;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2, len = 14 + (i % 3) * 6;
        ctx.beginPath(); ctx.moveTo(Math.cos(a) * 12, Math.sin(a) * 12);
        ctx.lineTo(Math.cos(a) * (12 + len), Math.sin(a) * (12 + len)); ctx.stroke();
      }
      ctx.restore();
    } else {
      ctx.save();
      const halo = ctx.createRadialGradient(tx, ty, 0, tx, ty, 30);
      halo.addColorStop(0, 'rgba(245,158,11,0.55)');
      halo.addColorStop(1, 'transparent');
      ctx.fillStyle = halo;
      ctx.beginPath(); ctx.arc(tx, ty, 30, 0, Math.PI * 2); ctx.fill();

      const flicker = Math.sin(Date.now() * 0.025) * 3;
      const fl = ctx.createLinearGradient(tx, ty + 8, tx, ty + 28 + flicker);
      fl.addColorStop(0, 'rgba(255,160,0,0.9)');
      fl.addColorStop(0.5, 'rgba(255,60,0,0.6)');
      fl.addColorStop(1, 'transparent');
      ctx.fillStyle = fl;
      ctx.beginPath();
      ctx.moveTo(tx - 5, ty + 8);
      ctx.lineTo(tx, ty + 28 + flicker);
      ctx.lineTo(tx + 5, ty + 8);
      ctx.fill();

      ctx.font = '34px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🚀', tx, ty - 6);
      ctx.restore();
    }

    if (pr && !pr.spawning) {
      drawParachutist(ctx, pr.x, pr.y, pr.t, pr.cashMult);
    }
  }, []);

  /* Cashout */
  const doCashout = useCallback((atMult) => {
    if (!activeBetRef.current || cashedRef.current) return;
    cashedRef.current = true;

    parachutistRef.current = {
      spawning: true,
      x: 0, y: 0,
      vx: 0, vy: 0,
      t: 0,
      cashMult: atMult,
    };

    const baseWin = Math.floor(activeBetRef.current * atMult);
    const win = applyMultiplier(baseWin);
    const betAmount = activeBetRef.current;
    addWinnings(win, 'crash', betAmount);
    setWonAt(atMult);
    setWonPts(win);
    setCashedOut(true);
    setActiveBet(null);
    activeBetRef.current = null;
    stopRocketSound();
    play(atMult >= 5 ? 'bigWin' : 'win');
    hapticFeedback?.('success');
  }, [play, stopRocketSound, applyMultiplier, addWinnings, hapticFeedback]);

  /* Waiting countdown */
  useEffect(() => {
    if (phase !== 'waiting') return;
    let t = Math.ceil(WAIT_MS / 1000);
    setCd(t);
    const iv = setInterval(() => {
      t--; setCd(t);
      if (t <= 0) { clearInterval(iv); startRound(); }
    }, 1000);
    return () => clearInterval(iv);
  }, [phase]);

  const startRound = useCallback(() => {
    crashRef.current = genCrash();
    startRef.current = performance.now();
    pointsRef.current = [{ t: 0, m: 1.00 }];
    cashedRef.current = false;
    parachutistRef.current = null;
    setMult(1.00); setWonAt(null); setWonPts(0); setCashedOut(false);
    setLostBet(null);
    startRocketSound();
    setPhase('flying');
  }, [startRocketSound]);

  /* Flying loop */
  useEffect(() => {
    if (phase !== 'flying') return;

    const tick = (now) => {
      const elapsed = (now - startRef.current) / 1000;
      const cur = Math.round(Math.exp(GROWTH * elapsed) * 100) / 100;
      pointsRef.current.push({ t: elapsed, m: cur });

      const pr = parachutistRef.current;
      if (pr && !pr.spawning) {
        pr.vy = Math.min(pr.vy + 0.10, 1.8);
        pr.vx *= 0.97;
        pr.x += pr.vx;
        pr.y += pr.vy;
        pr.t += 1;
      }

      const av = autoValRef.current;
      if (!cashedRef.current && activeBetRef.current && av >= 1.01 && cur >= av) {
        doCashout(cur);
      }

      if (cur >= crashRef.current) {
        const final = crashRef.current;
        if (!cashedRef.current && activeBetRef.current) {
          const lost = activeBetRef.current;
          activeBetRef.current = null;
          setActiveBet(null); setLostBet(lost);
          hapticFeedback?.('error');
        }
        setMult(final);
        setHistory(prev => [final, ...prev.slice(0, 9)]);
        setFlashRed(true); setTimeout(() => setFlashRed(false), 500);
        stopRocketSound();
        play('lose');
        drawCanvas(pointsRef.current, final, true);
        setPhase('crashed');
        return;
      }

      setMult(cur);
      // Update rocket sound pitch based on multiplier
      updateRocketSound(cur);
      drawCanvas(pointsRef.current, cur, false);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, doCashout, drawCanvas, play, updateRocketSound, stopRocketSound, hapticFeedback]);

  /* After crash -> waiting */
  useEffect(() => {
    if (phase !== 'crashed') return;
    const t = setTimeout(() => setPhase('waiting'), WAIT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  /* Draw stars during waiting */
  useEffect(() => {
    if (phase !== 'waiting') return;
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.fillStyle = '#030714'; ctx.fillRect(0, 0, W, H);
    const neb = ctx.createRadialGradient(W * .85, H * .15, 0, W * .85, H * .15, W * .5);
    neb.addColorStop(0, 'rgba(124,58,237,0.09)'); neb.addColorStop(1, 'transparent');
    ctx.fillStyle = neb; ctx.fillRect(0, 0, W, H);
    STARS.forEach(s => {
      ctx.beginPath(); ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${s.a})`; ctx.fill();
    });
  }, [phase]);

  /* Canvas resize */
  useEffect(() => {
    const resize = () => {
      const c = canvasRef.current; if (!c) return;
      c.width = c.parentElement.clientWidth;
      c.height = c.parentElement.clientHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  /* Place bet */
  const handleBet = useCallback(async () => {
    if (activeBetRef.current) return;
    if (!canSpin(bet)) {
      hapticFeedback?.('error');
      return;
    }
    const betSuccess = await useSpin(bet, 'crash');
    if (!betSuccess) return;

    hapticFeedback?.('medium');
    setActiveBet(bet); activeBetRef.current = bet;
    setCashedOut(false); setWonAt(null); setLostBet(null);
  }, [bet, canSpin, useSpin, hapticFeedback]);

  const handleCashout = () => {
    if (phase === 'flying' && activeBetRef.current && !cashedRef.current) doCashout(mult);
  };

  const isCrashed = phase === 'crashed';
  const isFlying = phase === 'flying';
  const isWaiting = phase === 'waiting';
  const canCashout = isFlying && !!activeBet && !cashedOut;
  const canBet = (isWaiting || isFlying) && !activeBet && !cashedOut;

  const multClr = isCrashed ? '#EF4444' : mult >= 10 ? '#A855F7' : mult >= 5 ? '#7C3AED' : mult >= 2 ? '#10B981' : '#F59E0B';

  return (
    <div style={{
      minHeight: '100vh',
      background: flashRed ? 'rgba(239,68,68,0.08)' : '#030714',
      padding: '16px',
      paddingBottom: '100px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: '12px',
          padding: '10px 16px',
          color: '#fff',
          fontSize: '16px',
          cursor: 'pointer',
        }}>← Back</button>
        <h1 style={{
          flex: 1,
          textAlign: 'center',
          color: '#F59E0B',
          fontSize: '24px',
          margin: 0,
        }}>🚀 Crash</h1>
      </div>

      {/* Balance */}
      <div style={{
        background: 'rgba(245,158,11,0.1)',
        border: '1px solid rgba(245,158,11,0.3)',
        borderRadius: '16px',
        padding: '16px',
        textAlign: 'center',
        marginBottom: '16px',
      }}>
        <div style={{ color: '#888', fontSize: '12px' }}>GAME BALANCE</div>
        <div style={{ color: '#F59E0B', fontSize: '28px', fontWeight: 'bold', marginBottom: 8 }}>
          {formatLilape(gameBalance)} LILAPE
        </div>
        <button
          onClick={() => setShowWallet(true)}
          style={{
            padding: '8px 20px', borderRadius: 8,
            background: 'linear-gradient(135deg, #10B981, #059669)',
            border: 'none', color: '#fff', fontSize: 13, fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          💰 Deposit / Withdraw
        </button>
      </div>

      {showWallet && <DepositWithdrawPanel onClose={() => setShowWallet(false)} gameBalanceHook={gameBalanceHook} />}

      {/* History */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {history.map((h, i) => (
          <span key={i} style={{
            padding: '4px 8px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 'bold',
            background: multColor(h) + '22',
            color: multColor(h),
            border: `1px solid ${multColor(h)}44`,
          }}>{fmtMult(h)}</span>
        ))}
      </div>

      {/* Canvas */}
      <div style={{
        position: 'relative',
        height: '200px',
        borderRadius: '16px',
        overflow: 'hidden',
        marginBottom: '16px',
      }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />

        {/* Multiplier overlay */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}>
          {isWaiting ? (
            <div>
              <div style={{ color: '#888', fontSize: '14px' }}>Next round in</div>
              <div style={{ color: '#fff', fontSize: '48px', fontWeight: 'bold' }}>{countdown}</div>
            </div>
          ) : (
            <div>
              <div style={{
                fontSize: '48px',
                fontWeight: 'bold',
                color: multClr,
                textShadow: `0 0 40px ${multClr}`,
              }}>{fmtMult(mult)}</div>
              {isCrashed && <div style={{ color: '#EF4444', fontWeight: 'bold' }}>FLEW AWAY</div>}
            </div>
          )}
        </div>

        {/* Win badge */}
        {wonAt && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'rgba(16,185,129,0.9)',
            padding: '8px 12px',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 'bold',
          }}>
            🎉 +{formatLilape(wonPts)} @ {fmtMult(wonAt)}
          </div>
        )}

        {/* Lost badge */}
        {isCrashed && lostBet && !cashedOut && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'rgba(239,68,68,0.9)',
            padding: '8px 12px',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 'bold',
          }}>
            💸 -{formatLilape(lostBet)}
          </div>
        )}
      </div>

      {/* Bet Options */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          BET AMOUNT
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {BET_OPTIONS.map(opt => (
            <button
              key={opt}
              onClick={() => !activeBet && setBet(opt)}
              disabled={!!activeBet}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: bet === opt ? '2px solid #F59E0B' : '1px solid rgba(255,255,255,0.2)',
                background: bet === opt ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.05)',
                color: bet === opt ? '#F59E0B' : '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: activeBet ? 'not-allowed' : 'pointer',
                opacity: activeBet ? 0.5 : 1,
              }}
            >{formatLilape(opt)}</button>
          ))}
        </div>
      </div>

      {/* Auto Cashout */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          AUTO CASHOUT AT
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {AUTO_TARGETS.map(t => (
            <button
              key={t}
              onClick={() => !activeBet && setAutoTarget(t)}
              disabled={!!activeBet}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: autoTarget === t ? '2px solid #7C3AED' : '1px solid rgba(255,255,255,0.2)',
                background: autoTarget === t ? 'rgba(124,58,237,0.2)' : 'rgba(255,255,255,0.05)',
                color: autoTarget === t ? '#7C3AED' : '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: activeBet ? 'not-allowed' : 'pointer',
                opacity: activeBet ? 0.5 : 1,
              }}
            >{t}×</button>
          ))}
        </div>
      </div>

      {/* Bet/Cashout Button */}
      {canCashout ? (
        <button
          onClick={handleCashout}
          style={{
            width: '100%',
            padding: '18px',
            borderRadius: '14px',
            border: 'none',
            background: 'linear-gradient(135deg, #10B981, #059669)',
            color: '#fff',
            fontSize: '18px',
            fontWeight: 'bold',
            cursor: 'pointer',
            animation: 'pulse 0.5s infinite',
          }}
        >
          💰 CASH OUT @ {fmtMult(mult)} (+{formatLilape(Math.floor(bet * mult))})
        </button>
      ) : (
        <button
          onClick={handleBet}
          disabled={!canBet || !canSpin(bet)}
          style={{
            width: '100%',
            padding: '18px',
            borderRadius: '14px',
            border: 'none',
            background: !canBet || !canSpin(bet) ? '#333' : 'linear-gradient(135deg, #F59E0B, #D97706)',
            color: '#fff',
            fontSize: '18px',
            fontWeight: 'bold',
            cursor: !canBet || !canSpin(bet) ? 'not-allowed' : 'pointer',
          }}
        >
          {activeBet ? `🚀 IN FLIGHT (${formatLilape(activeBet)})` : `BET ${formatLilape(bet)}`}
        </button>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `}</style>
    </div>
  );
}
